import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { catchError } from 'rxjs/operators';
import { PerformanceService } from '../services/performance.service';
import { DashboardService } from '../dashboard/dashboard.service';
import { HeaderService } from '../header/header.service';
import { AssessmentService } from '../dashboard/assessments/assessment.service';
import { DataService } from '../services/data.service';

@Injectable({
  providedIn: 'root'
})

export class LoginRegisterService {

  private baseUrl: string;
  private currentUserSubject: BehaviorSubject<any>;
  public currentUser: Observable<any>;

  constructor(
    private http: HttpClient,
    private performanceService: PerformanceService,
    private dashboardService: DashboardService,
    private headerService: HeaderService,
    private assessmentService: AssessmentService,
    private dataService: DataService,
    @Optional() @Inject('BASE_API_URL') baseUrl?: string
  ) {
    this.baseUrl = baseUrl ? baseUrl : '';
    this.currentUserSubject = new BehaviorSubject<any>(JSON.parse(localStorage.getItem('currentUser')));
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): any {
    return this.currentUserSubject.value;
  }

  forgotPassword(data: any, options?: any): Observable<any> {
    const url = `${this.baseUrl}auth/forgot-password`;
    return this.http.post(url, data, options);
  }

  loginRequest(data: any): Observable<any> {
    const url = `${this.baseUrl}auth/enterprise/login`;
    return this.http.post(url, data).pipe(
      tap(res => {
        if (['ENTERPRISE_ADMIN', 'ENTERPRISE_USER'].includes(res.role)) {
          localStorage.setItem('currentUser', JSON.stringify(res));
          localStorage.setItem('token', res.token);
          this.currentUserSubject.next(res);
        }
      })
    );
  }

  /** inviatedAdminRegister */
  inviatedAdminRegister(data: any, options?: any): Observable<any> {
    const url = `${this.baseUrl}auth/enterprise/accept-admin-invitation`;
    return this.http.post(url, data, options).pipe(
      tap((res: any) => {
        if (['ENTERPRISE_ADMIN', 'ENTERPRISE_USER'].includes(res.role)) {
          localStorage.setItem('currentUser', JSON.stringify(res));
          localStorage.setItem('token', res.token);
          this.currentUserSubject.next(res);
        }
      })
    );
  }

  isAdminExist(token): Observable<any> {
    const url = `${this.baseUrl}auth/enterprise/validate-admin-token?token=`;
    return this.http.get(url + encodeURIComponent(token));
  }

  /** signup request */
  signUpRequest(data: any): Observable<any> {
    const url = `${this.baseUrl}auth/enterprise/signup`;
    return this.http.post(url, data);
  }

  /** reset password */
  resetPassword(data: any, options?: any): Observable<any> {
    const url = `${this.baseUrl}auth/reset-password`;
    return this.http.post(url, data, options);
  }

  /** logout  */
  logout(): Observable<any> {
    const url = `${this.baseUrl}auth/logout`;
    return this.http.post(url, {}).pipe(
      tap((data: any) => {
        this.clearAllSessionData();
      }),
      catchError((error) => {
        // Even if backend logout fails, clear local session data
        this.clearAllSessionData();
        throw error;
      })
    );
  }

  /** Clear all session data and localStorage */
  private clearAllSessionData(): void {
    // Clear all local and session storage items
    localStorage.clear();
    sessionStorage.clear();

    // Clear all performance service caches
    this.performanceService.clearAllCache();

    // Clear dashboard service caches
    this.dashboardService.clearAdminProfileCache();

    // Reset header service notifications
    this.headerService.notificationsCount$.next(0);

    // Clear assessment service current assessment
    this.assessmentService.clearCurrentAssessment();

    // Clear data service user data
    this.dataService.clearUserData();

    // Reset the current user subject
    this.currentUserSubject.next(null);
  }

  /** Public method to clear session data (can be called from other components) */
  public clearSessionData(): void {
    this.clearAllSessionData();
  }

}
